import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import ImageUpload from '../../components/admin/ImageUpload';

const MediaManager = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    type: 'Podcast',
    date: '',
    platform: '',
    description: '',
    url: '',
    thumbnail_url: '',
    start_time: '',
    end_time: ''
  });
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/media`);
      const data = await response.json();
      if (data.success) setMedia(data.media);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const url = editingId
        ? `${process.env.REACT_APP_BACKEND_URL}/api/admin/media/${editingId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/admin/media`;
      
      await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      await fetchMedia();
      resetForm();
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setFormData(item);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this media item?')) return;
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/media/${id}`, { method: 'DELETE' });
    await fetchMedia();
  };

  const resetForm = () => {
    setFormData({ title: '', type: 'Podcast', date: '', platform: '', description: '', url: '', thumbnail_url: '', start_time: '', end_time: '' });
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', fontWeight: '700' }}>Media Manager</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {showForm ? <X size={20} /> : <Plus size={20} />}
          {showForm ? 'Cancel' : 'Add Media'}
        </button>
      </div>

      {showForm && (
        <div style={{ backgroundColor: '#FFFFFF', padding: '32px', borderRadius: '8px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '24px' }}>{editingId ? 'Edit' : 'New'} Media</h2>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gap: '20px' }}>
              <input type="text" placeholder="Title *" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required style={{ width: '100%', padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '4px' }} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} style={{ padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '4px' }}>
                  <option>Podcast</option>
                  <option>Video</option>
                  <option>Conference Talk</option>
                  <option>Interview</option>
                </select>
                <input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required style={{ padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '4px' }} />
                <input type="text" placeholder="Platform *" value={formData.platform} onChange={(e) => setFormData({ ...formData, platform: e.target.value })} required style={{ padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '4px' }} />
              </div>
              <textarea placeholder="Description *" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required rows="3" style={{ width: '100%', padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '4px', fontFamily: 'var(--font-body)' }} />
              <input type="url" placeholder="Media URL (e.g. YouTube link) *" value={formData.url} onChange={(e) => setFormData({ ...formData, url: e.target.value })} required style={{ width: '100%', padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '4px' }} />
              <ImageUpload label="Thumbnail (upload from your computer, or paste a URL)" value={formData.thumbnail_url} onChange={(url) => setFormData({ ...formData, thumbnail_url: url })} testid="media-thumbnail" />
              <div>
                <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '600', marginBottom: '8px', color: '#111111' }}>Playback range (YouTube only, optional)</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <input type="text" placeholder="Start time e.g. 1:30 or 90" value={formData.start_time || ''} onChange={(e) => setFormData({ ...formData, start_time: e.target.value })} data-testid="media-start-time" style={{ padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '4px' }} />
                  <input type="text" placeholder="End time e.g. 3:45 or 225" value={formData.end_time || ''} onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} data-testid="media-end-time" style={{ padding: '10px 14px', border: '1px solid #E0E0E0', borderRadius: '4px' }} />
                </div>
                <p style={{ fontSize: '0.8rem', color: '#666666', marginTop: '6px' }}>Use minutes:seconds (1:30) or plain seconds (90). Leave blank to play the full video.</p>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" disabled={loading} className="btn-primary"><Save size={18} /> {loading ? 'Saving...' : 'Save'}</button>
                <button type="button" onClick={resetForm} style={{ padding: '12px 24px', border: '1px solid #E0E0E0', borderRadius: '4px', backgroundColor: 'transparent' }}>Cancel</button>
              </div>
            </div>
          </form>
        </div>
      )}

      <div style={{ display: 'grid', gap: '16px' }}>
        {loading && !showForm ? <div style={{ textAlign: 'center', color: '#666666' }}>Loading...</div> : media.length === 0 ? <div style={{ textAlign: 'center', color: '#666666', padding: '32px', backgroundColor: '#FFFFFF', borderRadius: '8px' }}>No media yet.</div> : media.map((item) => (
          <div key={item.id} style={{ backgroundColor: '#FFFFFF', padding: '20px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
            <div>
              <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: '#111111', color: '#F4F1E8', marginRight: '12px' }}>{item.type}</span>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginTop: '8px' }}>{item.title}</h3>
              <p style={{ fontSize: '0.85rem', color: '#666666', marginTop: '4px' }}>{item.platform} • {new Date(item.date).toLocaleDateString()}</p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => handleEdit(item)} style={{ padding: '8px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#111111' }}><Edit2 size={18} /></button>
              <button onClick={() => handleDelete(item.id)} style={{ padding: '8px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', color: '#cc0000' }}><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MediaManager;
